/**
 * @cfleet/auth - Type definitions
 *
 * This file contains type definitions for authentication state management.
 * Supports both local JWT auth (development) and better-auth (staging/production).
 *
 * Multi-App Support:
 * - Dispatcher app: /auth/* endpoints, `token` and `user` fields
 * - Driver app: /driver/auth/* endpoints, `token` and `driver` fields
 * - Customer app: /customer/auth/* endpoints, `token` and `customer` fields
 */

// ─────────────────────────────────────────────────────────────────────────────
// Multi-App Authentication Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * App type identifier for multi-app auth configuration
 */
export type AppType = 'dispatcher' | 'driver' | 'customer';

/**
 * Unified profile that normalizes user/driver/customer across apps.
 * App-specific fields are stored in metadata for access when needed.
 */
export interface AuthProfile {
  /** Unique identifier */
  id: string;
  /** Full name */
  name: string;
  /** Email address */
  email: string;
  /** Role (defaults to app type if not present) */
  role?: string;
  /** Avatar URL */
  avatar?: string;
  /** Phone number */
  phone?: string;
  /** Original profile data from API (app-specific fields) */
  metadata?: Record<string, unknown>;
}

/**
 * Configuration for app-specific auth behavior
 */
export interface AppAuthConfig {
  /** App type identifier */
  appType: AppType;
  /** Auth endpoint paths */
  endpoints: {
    login: string;
    logout: string;
    refresh: string;
    session?: string;
  };
  /** Field name for access token in response ('token' or 'access_token') */
  accessTokenField: 'token' | 'access_token';
  /** Field name for profile in response ('user', 'driver', 'customer') */
  profileField: 'user' | 'driver' | 'customer';
}

/**
 * Pre-configured auth configs for each app type.
 * These match the backend endpoint structure and response formats.
 */
export const APP_AUTH_CONFIGS: Record<AppType, AppAuthConfig> = {
  dispatcher: {
    appType: 'dispatcher',
    endpoints: {
      login: '/auth/login',
      logout: '/auth/logout',
      refresh: '/auth/refresh',
      session: '/users/me',
    },
    accessTokenField: 'token',
    profileField: 'user',
  },
  driver: {
    appType: 'driver',
    endpoints: {
      login: '/driver/auth/login',
      logout: '/driver/auth/logout',
      refresh: '/driver/auth/refresh',
      session: '/driver/auth/session',
    },
    accessTokenField: 'token',
    profileField: 'driver',
  },
  customer: {
    appType: 'customer',
    endpoints: {
      login: '/customer/auth/login',
      logout: '/customer/auth/logout',
      refresh: '/customer/auth/refresh',
      session: '/customer/auth/session',
    },
    accessTokenField: 'token',
    profileField: 'customer',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// User and Role Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * User role in the system
 */
export type UserRole = 'Admin' | 'Manager' | 'Dispatcher' | 'Driver' | 'Customer';

/**
 * User account status
 */
export type UserStatus = 'Active' | 'Inactive' | 'Suspended' | 'Pending';

/**
 * Granular user permissions
 */
export interface UserPermissions {
  /** Can view client information */
  viewClients: boolean;
  /** Can view audit logs */
  viewAudit: boolean;
  /** Can view analytics and reports */
  viewAnalytics: boolean;
  /** Can create, update, and deactivate drivers */
  manageDrivers: boolean;
  /** Can create, update, and dispatch routes */
  manageRoutes: boolean;
  /** Can create and manage orders */
  manageOrders: boolean;
}

/**
 * Authenticated user information
 */
export interface User {
  /** Unique user identifier */
  id: string;
  /** User's full name */
  name: string;
  /** User's email address */
  email: string;
  /** User's role in the system */
  role: UserRole;
  /** User account status */
  status?: UserStatus;
  /** Granular permission flags */
  permissions: UserPermissions;
  /** URL to user's avatar image */
  avatar?: string;
  /** Account creation timestamp */
  created_at?: string;
  /** Last successful login timestamp */
  last_login?: string;
  /** Organizations the user belongs to (multi-tenant) */
  organizations?: Organization[];
}

/**
 * Organization/tenant information for multi-tenancy
 */
export interface Organization {
  /** Unique organization identifier */
  id: string;
  /** Organization name */
  name: string;
  /** User's role within this organization */
  role: UserRole;
}

/**
 * Authentication tokens
 */
export interface AuthTokens {
  /** JWT access token */
  accessToken: string;
  /** Refresh token for obtaining new access tokens */
  refreshToken: string;
  /** Token expiry time in seconds */
  expiresIn: number;
  /** Timestamp when the access token expires */
  expiresAt: number;
}

/**
 * Authentication state
 */
export interface AuthState {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication is being checked/loaded */
  isLoading: boolean;
  /** Current authenticated user */
  user: User | null;
  /** Current authentication tokens */
  tokens: AuthTokens | null;
  /** Current active organization (for multi-tenant) */
  currentOrganization: Organization | null;
  /** List of organizations the user belongs to */
  organizations: Organization[];
  /** Authentication error message */
  error: string | null;
}

/**
 * Login credentials with app-specific options
 */
export interface LoginCredentials {
  /** User's email address */
  email: string;
  /** User's password */
  password: string;
  /** Customer-specific: Remember me extends token expiry to 30 days */
  rememberMe?: boolean;
  /** Driver-specific: Device ID for session tracking */
  deviceId?: string;
}

/**
 * Raw user data from API responses (lowercase roles from backend)
 * This is the format returned by POST /auth/login, POST /auth/accept-invite, etc.
 */
export interface RawApiUser {
  id: string;
  name: string;
  email: string;
  /** Backend returns lowercase roles */
  role: 'admin' | 'manager' | 'dispatcher' | 'viewer' | 'driver' | 'customer';
  status?: 'Active' | 'Inactive' | 'Pending';
  permissions?: Record<string, boolean>;
  avatar?: string;
  created_at?: string;
  last_login?: string;
  organizations?: Array<{
    id: string;
    name: string;
    role: string;
  }>;
}

/**
 * Login response from the API
 * Uses RawApiUser since backend returns lowercase roles
 */
export interface LoginResponse {
  /** JWT access token */
  token: string;
  /** Refresh token */
  refresh_token: string;
  /** Token expiry in seconds */
  expires_in: number;
  /** Authenticated user information (raw from API with lowercase role) */
  user: RawApiUser;
}

/**
 * Auth provider type - determines which authentication backend to use
 */
export type AuthProviderType = 'local' | 'better-auth';

/**
 * Auth provider configuration
 */
export interface AuthProviderConfig {
  /** Type of auth provider */
  type: AuthProviderType;
  /** API base URL for auth endpoints */
  apiBaseUrl: string;
  /** Storage key prefix for tokens */
  storageKeyPrefix?: string;
  /** Whether to persist auth state to localStorage */
  persistAuth?: boolean;
  /** Token refresh threshold in seconds (refresh when this much time remains) */
  refreshThreshold?: number;
}

/**
 * Auth context value provided to consumers
 */
export interface AuthContextValue {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication is being checked/loaded */
  isLoading: boolean;
  /** Current authenticated user */
  user: User | null;
  /** Current authentication tokens */
  tokens: AuthTokens | null;
  /** Current active organization (for multi-tenant) */
  currentOrganization: Organization | null;
  /** List of organizations the user belongs to */
  organizations: Organization[];
  /** Authentication error message */
  error: string | null;
  /** Login with email and password */
  login: (credentials: LoginCredentials) => Promise<void>;
  /** Logout and clear auth state */
  logout: () => Promise<void>;
  /** Refresh the access token */
  refreshToken: () => Promise<void>;
  /** Switch to a different organization (multi-tenant) */
  switchOrganization: (organizationId: string) => Promise<void>;
  /** Check if user has a specific permission */
  hasPermission: (permission: keyof UserPermissions) => boolean;
  /** Check if user has a specific role */
  hasRole: (role: UserRole | UserRole[]) => boolean;
  /** Get the current access token (for API calls) */
  getAccessToken: () => string | null;
  /** Clear any auth errors */
  clearError: () => void;
  /**
   * Set auth state from a login response (for accept-invite, external auth flows)
   * This allows setting tokens and user from external API responses without going through login()
   */
  setAuthFromLoginResponse: (response: LoginResponse) => Promise<void>;
  /** Immediately invalidate session without server logout (for middleware 401s) */
  triggerSessionInvalidation: () => Promise<void>;
  /** Whether a session invalidation is in progress (suppresses error rendering) */
  isSessionInvalidating: boolean;
  /**
   * Whether a token refresh is currently retrying after a transient failure.
   * Drives a non-destructive "reconnecting" UI instead of an abrupt logout.
   */
  isReconnecting: boolean;
}

/**
 * Default permissions for a new user
 */
export const DEFAULT_PERMISSIONS: UserPermissions = {
  viewClients: false,
  viewAudit: false,
  viewAnalytics: false,
  manageDrivers: false,
  manageRoutes: false,
  manageOrders: false,
};

/**
 * Initial auth state
 */
export const INITIAL_AUTH_STATE: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  tokens: null,
  currentOrganization: null,
  organizations: [],
  error: null,
};
